import { createServerFn } from '@tanstack/react-start'
import type {
  DispatchOutcomeStatus,
  DispatchTargetType,
} from '@rapid-response/shared'
import { rpcAuthMiddleware } from './rpc-auth-middleware'
import {
  clearDispatchOutcomeHandler,
  createDispatchSessionHandler,
  getDispatchSessionHandler,
  getJobsHandler,
  getOperativesHandler,
  listDispatchSessionsHandler,
  recordDispatchOutcomeHandler,
  resolveDispatchPropertyHandler,
} from './dispatch.handlers'

// RPC wrappers for the dispatch feature — thin delegates to ./dispatch.handlers
// (where the testable logic lives). Data-access is in @rapid-response/api and
// shared with the REST routes under /api/v1/dispatch/* (UI/API parity).

export const resolveDispatchProperty = createServerFn({ method: 'POST' })
  .middleware([rpcAuthMiddleware])
  .inputValidator((d: { jobNumber: string }) => d)
  .handler(async ({ data, context }) =>
    resolveDispatchPropertyHandler({ caller: context.caller, ...data }),
  )

export const getNearestOperatives = createServerFn({ method: 'POST' })
  .middleware([rpcAuthMiddleware])
  .inputValidator((d: { jobNumber: string; sessionId?: string | null; refresh?: boolean }) => d)
  .handler(async ({ data, context }) =>
    getOperativesHandler({ caller: context.caller, ...data }),
  )

export const getNearbyJobs = createServerFn({ method: 'POST' })
  .middleware([rpcAuthMiddleware])
  .inputValidator(
    (d: { jobNumber: string; radius: number; interval: number; sessionId?: string | null; refresh?: boolean }) => d,
  )
  .handler(async ({ data, context }) =>
    getJobsHandler({ caller: context.caller, ...data }),
  )

export const createDispatchSession = createServerFn({ method: 'POST' })
  .middleware([rpcAuthMiddleware])
  .inputValidator(
    (d: { jobNumber: string; assetNumber: number; radius: number; interval: number; assetLat?: number | null; assetLon?: number | null }) => d,
  )
  .handler(async ({ data, context }) =>
    createDispatchSessionHandler({ caller: context.caller, ...data }),
  )

export const getDispatchSession = createServerFn({ method: 'POST' })
  .middleware([rpcAuthMiddleware])
  .inputValidator((d: { id: string }) => d)
  .handler(async ({ data, context }) =>
    getDispatchSessionHandler({ caller: context.caller, id: data.id }),
  )

export const listDispatchSessions = createServerFn({ method: 'GET' })
  .middleware([rpcAuthMiddleware])
  .handler(async ({ context }) =>
    listDispatchSessionsHandler({ caller: context.caller }),
  )

export const recordDispatchOutcome = createServerFn({ method: 'POST' })
  .middleware([rpcAuthMiddleware])
  .inputValidator(
    (d: {
      sessionId: string
      targetType: DispatchTargetType
      targetKey: string
      label: string
      status: DispatchOutcomeStatus
      acceptedJob?: string | null
    }) => d,
  )
  .handler(async ({ data, context }) =>
    recordDispatchOutcomeHandler({ caller: context.caller, ...data }),
  )

export const clearDispatchOutcome = createServerFn({ method: 'POST' })
  .middleware([rpcAuthMiddleware])
  .inputValidator(
    (d: { sessionId: string; targetType: DispatchTargetType; targetKey: string }) => d,
  )
  .handler(async ({ data, context }) =>
    clearDispatchOutcomeHandler({ caller: context.caller, ...data }),
  )
